/**
 * Payment Profile Check Component
 *
 * Validates and prompts users to set up payment profile before
 * submitting expense reports. Shows current profile status and
 * provides navigation to profile setup.
 */

import { apiClient } from '../services/api-client.js';
import type { UserPaymentProfile } from '../../backend/types/database.js';

export interface PaymentProfileCheckProps {
  onProfileComplete?: (profile: UserPaymentProfile) => void;
  onCancel?: () => void;
  className?: string;
}

/**
 * Check if user has a payment profile
 */
export async function checkPaymentProfile(): Promise<UserPaymentProfile | null> {
  try {
    const response = await apiClient.get('/payment-profiles/me');
    return response.data as UserPaymentProfile;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null; // No profile exists
    }
    throw error;
  }
}

/**
 * Create payment profile check modal
 */
export function createPaymentProfileCheckModal(props: PaymentProfileCheckProps = {}): HTMLElement {
  const { onProfileComplete, onCancel, className = '' } = props;

  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4';

  // Create modal container
  const modal = document.createElement('div');
  modal.className = `bg-neutral-900 border border-neutral-800 rounded-lg shadow-2xl max-w-md w-full ${className}`;

  // Header
  const header = document.createElement('div');
  header.className = 'p-6 border-b border-neutral-800';

  const icon = document.createElement('div');
  icon.className = 'w-12 h-12 bg-amber-900/20 rounded-full flex items-center justify-center mb-4';
  icon.innerHTML = `
    <svg class="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  `;

  const title = document.createElement('h3');
  title.className = 'text-lg font-semibold text-neutral-100';
  title.textContent = 'Payment Profile Required';

  const description = document.createElement('p');
  description.className = 'text-sm text-neutral-400 mt-2';
  description.textContent = 'You need to set up your payment profile before submitting expense reports. This ensures reimbursements can be processed correctly.';

  header.appendChild(icon);
  header.appendChild(title);
  header.appendChild(description);
  modal.appendChild(header);

  // Content
  const content = document.createElement('div');
  content.className = 'p-6 space-y-4';

  // Benefits list
  const benefits = createBenefitsList();
  content.appendChild(benefits);

  // Status indicator
  const statusDiv = document.createElement('div');
  statusDiv.className = 'bg-neutral-800/50 rounded-lg p-4';
  statusDiv.innerHTML = `
    <div class="flex items-center gap-3">
      <div class="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
      <span class="text-sm text-neutral-300">Checking payment profile status...</span>
    </div>
  `;
  content.appendChild(statusDiv);

  modal.appendChild(content);

  // Footer
  const footer = document.createElement('div');
  footer.className = 'p-6 border-t border-neutral-800 bg-neutral-900/50';

  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'flex justify-end gap-3';

  // Cancel button
  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'px-4 py-2 text-neutral-400 hover:text-neutral-100 transition-colors';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.onclick = () => {
    overlay.remove();
    onCancel?.();
  };

  // Setup button
  const setupBtn = document.createElement('button');
  setupBtn.className = 'px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-all';
  setupBtn.textContent = 'Set Up Payment Profile';
  setupBtn.onclick = () => {
    // Navigate to payment profile setup
    window.location.hash = '/payment-profile';
    overlay.remove();
  };

  buttonContainer.appendChild(cancelBtn);
  buttonContainer.appendChild(setupBtn);
  footer.appendChild(buttonContainer);
  modal.appendChild(footer);

  overlay.appendChild(modal);

  // Check profile status after modal is shown
  checkProfileStatus(statusDiv, setupBtn, onProfileComplete);

  return overlay;
}

/**
 * Create payment profile prompt component (inline version)
 */
export function createPaymentProfilePrompt(props: PaymentProfileCheckProps = {}): HTMLElement {
  const { onProfileComplete, className = '' } = props;

  const container = document.createElement('div');
  container.className = `bg-amber-900/20 border border-amber-800/50 rounded-lg p-4 ${className}`;

  const content = document.createElement('div');
  content.className = 'flex items-start gap-3';

  // Icon
  const iconDiv = document.createElement('div');
  iconDiv.className = 'flex-shrink-0';
  iconDiv.innerHTML = `
    <svg class="w-5 h-5 text-amber-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  `;

  // Message
  const messageDiv = document.createElement('div');
  messageDiv.className = 'flex-1 space-y-2';

  const title = document.createElement('p');
  title.className = 'text-sm font-medium text-amber-400';
  title.textContent = 'Payment Profile Required';

  const description = document.createElement('p');
  description.className = 'text-sm text-neutral-400';
  description.textContent = 'Please set up your payment profile to enable expense report submissions.';

  const linkBtn = document.createElement('button');
  linkBtn.className = 'text-sm text-amber-400 hover:text-amber-300 underline transition-colors mt-2';
  linkBtn.textContent = 'Set up payment profile →';
  linkBtn.onclick = () => {
    window.location.hash = '/payment-profile';
  };

  messageDiv.appendChild(title);
  messageDiv.appendChild(description);
  messageDiv.appendChild(linkBtn);

  content.appendChild(iconDiv);
  content.appendChild(messageDiv);
  container.appendChild(content);

  // Check profile periodically and hide if complete
  const checkInterval = setInterval(async () => {
    try {
      const profile = await checkPaymentProfile();
      if (profile) {
        clearInterval(checkInterval);
        container.style.display = 'none';
        onProfileComplete?.(profile);
      }
    } catch (error) {
      console.error('Failed to check payment profile:', error);
    }
  }, 5000); // Check every 5 seconds

  // Clean up interval when element is removed
  const observer = new MutationObserver(() => {
    if (!document.contains(container)) {
      clearInterval(checkInterval);
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return container;
}

/**
 * Create benefits list
 */
function createBenefitsList(): HTMLElement {
  const list = document.createElement('ul');
  list.className = 'space-y-2';

  const benefits = [
    'Direct bank transfers for faster reimbursements',
    'Secure storage of your payment information',
    'One-time setup for all future expense reports'
  ];

  benefits.forEach(benefit => {
    const item = document.createElement('li');
    item.className = 'flex items-start gap-2';

    const icon = document.createElement('span');
    icon.className = 'text-green-500 mt-0.5';
    icon.innerHTML = `
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
      </svg>
    `;

    const text = document.createElement('span');
    text.className = 'text-sm text-neutral-300';
    text.textContent = benefit;

    item.appendChild(icon);
    item.appendChild(text);
    list.appendChild(item);
  });

  return list;
}

/**
 * Check profile status and update UI
 */
async function checkProfileStatus(
  statusDiv: HTMLElement,
  setupBtn: HTMLButtonElement,
  onProfileComplete?: (profile: UserPaymentProfile) => void
): Promise<void> {
  try {
    const profile = await checkPaymentProfile();

    if (profile) {
      // Profile exists - show success
      statusDiv.innerHTML = `
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span class="text-sm text-green-400">Payment profile already configured!</span>
        </div>
      `;
      setupBtn.textContent = 'Continue';
      setupBtn.onclick = () => {
        const overlay = statusDiv.closest('.fixed');
        if (overlay) overlay.remove();
        onProfileComplete?.(profile);
      };
    } else {
      // No profile - show setup required
      statusDiv.innerHTML = `
        <div class="flex items-center gap-3">
          <div class="w-2 h-2 bg-red-500 rounded-full"></div>
          <span class="text-sm text-neutral-300">No payment profile found</span>
        </div>
      `;
    }
  } catch (error) {
    console.error('Failed to check payment profile:', error);
    statusDiv.innerHTML = `
      <div class="flex items-center gap-3">
        <svg class="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-sm text-red-400">Failed to check profile status</span>
      </div>
    `;
  }
}

/**
 * Validate payment profile before submission
 * Returns true if profile exists, false if not
 */
export async function validatePaymentProfileForSubmission(): Promise<boolean> {
  const profile = await checkPaymentProfile();

  if (!profile) {
    // Show modal prompting to set up profile
    const modal = createPaymentProfileCheckModal({
      onProfileComplete: () => {
        // Profile setup complete, can proceed with submission
        return true;
      }
    });
    document.body.appendChild(modal);
    return false;
  }

  return true;
}